import { Box, Grid, Link, Typography } from '@material-ui/core'
import { memo, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { shallowEqual, useSelector } from 'react-redux'
import { Link as RouterLink } from 'react-router-dom'
import { AppState } from 'store/store'
import useURLParams from 'utils/URLParamsContex'
import colors from 'utils/colors'

type FooterItem = { i18Key: string; link: string }
type GlobalConfiguration = {
    title: string
    icons: { footer: string }
    footer: FooterItem[]
    showFooterLogo?: boolean
}
type Customize = { globalConfiguration: GlobalConfiguration }

const currentYear = new Date().getFullYear()

const Footer = memo(function Footer(): JSX.Element {
    const customize = useSelector<AppState, Customize | undefined>((s) => s.generalState.customizeJsData, shallowEqual)
    const { t } = useTranslation()
    const { B2B } = useURLParams()

    const gc = customize?.globalConfiguration
    const appTitle = gc?.title ?? ''
    const footerLogoSrc = useMemo(
        () =>
            gc?.showFooterLogo && gc.icons?.footer
                ? `${process.env.PUBLIC_URL}/customize/${gc.icons.footer}`
                : undefined,
        [gc?.showFooterLogo, gc?.icons?.footer],
    )

    return (
        <Box
            component="footer"
            role="contentinfo"
            aria-label={t('footer.landmark', { defaultValue: 'Seitenfuß' }) as string}
            bgcolor={colors.lightGray}
            color={colors.black}
            pt={2}
            pb={2}
            minHeight={{ xs: 80, sm: 40 }}
        >
            <Grid container alignItems="stretch" wrap="nowrap">
                <Grid
                    item
                    xs={12}
                    md={3}
                    container
                    alignItems="center"
                    justifyContent="center"
                    className="FooterIconWrapper"
                    aria-label={t('footer.brandArea', { defaultValue: 'Branding-Bereich' }) as string}
                >
                    <Box textAlign={{ xs: 'center', sm: 'left' }} mb={{ xs: 2, md: 0 }}>
                        {footerLogoSrc ? (
                            <img
                                src={footerLogoSrc}
                                alt={t('footer.logoAlt', { defaultValue: 'Footer-Logo' }) as string}
                                className="FooterIcon"
                                loading="lazy"
                                decoding="async"
                            />
                        ) : (
                            <span aria-hidden="true" />
                        )}
                    </Box>
                </Grid>

                <Grid
                    item
                    xs
                    container
                    className="FooterLinkWrapper"
                    style={{ position: 'relative' }}
                    role="navigation"
                    aria-label={t('footer.links', { defaultValue: 'Footer-Navigation' }) as string}
                >
                    <Grid
                        container
                        spacing={2}
                        alignItems="center"
                        role="list"
                        aria-label={t('footer.linkList', { defaultValue: 'Liste mit Footer-Links' }) as string}
                    >
                        {gc?.footer?.map((item, idx) => {
                            const isCopyright = item.i18Key === 'copyright'
                            const text = `${isCopyright ? `© ${currentYear} ` : ''}${t(item.i18Key) as string}`
                            const key = `${item.i18Key}-${item.link || idx}`

                            return (
                                <Box key={key} display="contents">
                                    {!B2B && item.i18Key === 'copyright' && (
                                        <Grid item role="listitem" style={{ marginRight: 12 }}>
                                            <Link
                                                className="FooterLink"
                                                color="inherit"
                                                component={RouterLink}
                                                to={'/widerruf'}
                                                aria-label={t('withdrawalStrings.buttonLabel') as string}
                                            >
                                                {t('withdrawalStrings.buttonLabel')}
                                            </Link>
                                        </Grid>
                                    )}
                                    <Grid item role="listitem" style={{ marginRight: 12 }}>
                                        {item.link ? (
                                            <Link
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="FooterLink"
                                                href={item.link}
                                                color="inherit"
                                                aria-label={text}
                                            >
                                                {text}
                                            </Link>
                                        ) : (
                                            <Typography
                                                className="FooterLink"
                                                color="inherit"
                                                variant="h5"
                                                component="span"
                                                aria-label={text}
                                            >
                                                {text}
                                            </Typography>
                                        )}
                                    </Grid>
                                </Box>
                            )
                        })}
                    </Grid>

                    <Typography
                        className="FooterLink"
                        color="primary"
                        variant="subtitle2"
                        style={{ position: 'absolute', right: 0, bottom: 0, display: 'none' }}
                        aria-hidden="true"
                    >
                        Version: {appTitle} — Iteration: {process.env.REACT_APP_BUILD_NUMBER}
                    </Typography>
                </Grid>
            </Grid>
        </Box>
    )
})

export default Footer
